import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type SellerComplaintRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { AdminError } from '@/features/admin/AdminError';

/**
 * What buyers have said about this pharmacy's own orders.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * Nothing. A buyer complained, ChiCare decided, and the pharmacy that
 * dispensed the medicine found out when money went missing from its statement
 * — no complaint, no reason, no chance to answer.
 *
 * ── WHY A LIST AND NOT CARDS ────────────────────────────────────────────────
 *
 * This was a grid of cards for about a day. Cards are good for six things and
 * useless for six hundred: one claim per tile means a pharmacy with a year of
 * history scrolls past acres of whitespace to reach the one it wanted, and no
 * two rows line up for comparing.
 *
 * A complaints ledger is a RECORD, and it wants the properties of a record:
 * dense rows, newest first, the same field in the same column every time, and
 * a search box that still works in three years with two thousand rows in it.
 *
 * The tiles and the shelves above it stay. Those answer "what needs me today",
 * which is a different question from "find me the one from March" — and the
 * screen has to answer both.
 *
 * ── AND WHY IT IS NEVER A SCOLDING ──────────────────────────────────────────
 *
 * Most complaints are nobody's fault, and a pharmacy reading this has usually
 * done nothing wrong. A row that needs an answer carries a soft amber wash and
 * an amber chip that says so — never red, and never an edge stripe. State is
 * said in words and in the fill, not with a bar bolted to the side of a box.
 */

type Shelf = 'needs_you' | 'answered' | 'settled' | 'refunded' | 'all';
type Sort = 'newest' | 'oldest' | 'largest';

const SHELVES: Array<{ key: Shelf; label: string }> = [
  { key: 'needs_you', label: 'Not answered' },
  { key: 'answered', label: 'Answered' },
  { key: 'settled', label: 'Settled' },
  { key: 'refunded', label: 'Refunded' },
  { key: 'all', label: 'Everything' },
];

const SORTS: Array<{ key: Sort; label: string }> = [
  { key: 'newest', label: 'Newest first' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'largest', label: 'Largest claim' },
];

/** Which shelves a given claim belongs on. A claim can sit on two. */
function shelvesFor(one: SellerComplaintRow): Shelf[] {
  const shelves: Shelf[] = ['all'];
  const decided = one.decided_at !== null;

  if (one.unanswered) {
    shelves.push('needs_you');
  } else if (!decided && one.responded_at) {
    // Answered, and now waiting on us. The pharmacy has done everything it
    // can here, which is worth separating from the pile that still needs them.
    shelves.push('answered');
  }

  if (decided) {
    shelves.push('settled');

    // Money actually left: a refund, or a return that ends in one. A
    // replacement costs them a pack, not a payment, and "not upheld" costs
    // them nothing — lumping those together would overstate the damage.
    if (one.resolution === 'refund' || one.resolution === 'return') {
      shelves.push('refunded');
    }
  }

  return shelves;
}

/**
 * Everything on a row somebody might type into the box.
 *
 * Deliberately generous: a pharmacy hunting "that amoxicillin one" should find
 * it, and so should one reading a reference down the phone to support.
 */
function haystack(one: SellerComplaintRow): string {
  return [
    one.reference,
    one.order_reference,
    one.buyer,
    one.reason_label,
    one.resolution_label,
    one.claimed_display,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function SellerComplaintsScreen() {
  const [shelf, setShelf] = useState<Shelf>('needs_you');
  const [sort, setSort] = useState<Sort>('newest');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<SellerComplaintRow | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['seller', 'complaints'],
    queryFn: ({ signal }) => api.sellerComplaints(signal),
    refetchInterval: 120_000,
  });

  const complaints = useMemo(() => data?.complaints ?? [], [data]);

  const counts = useMemo(() => {
    const tally: Record<Shelf, number> = {
      needs_you: 0,
      answered: 0,
      settled: 0,
      refunded: 0,
      all: 0,
    };

    for (const one of complaints) {
      for (const key of shelvesFor(one)) {
        tally[key] += 1;
      }
    }

    return tally;
  }, [complaints]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();

    const rows = complaints.filter(
      (one) => shelvesFor(one).includes(shelf) && (needle === '' || haystack(one).includes(needle)),
    );

    // Newest first, and said explicitly here rather than trusting whatever
    // order the server happened to send.
    return [...rows].sort((a, b) => {
      if (sort === 'largest') {
        return b.claimed_kobo - a.claimed_kobo;
      }

      const at = a.reported_at ? Date.parse(a.reported_at) : 0;
      const bt = b.reported_at ? Date.parse(b.reported_at) : 0;

      return sort === 'oldest' ? at - bt : bt - at;
    });
  }, [complaints, shelf, search, sort]);

  if (isError) {
    return (
      <AdminError title="We couldn't load your complaints" error={error} onRetry={() => refetch()} />
    );
  }

  const searching = search.trim() !== '';

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 xl:px-8">
      <header className="mb-5">
        <h1 className="m-0 font-display text-[26px] font-bold tracking-[-0.02em]">Complaints</h1>
        <p className="m-0 mt-1 max-w-[62ch] text-[13px] leading-relaxed text-muted">
          What buyers have reported about your orders. We decide these under the returns policy,
          so nothing here waits on you — but your side is read whenever it reaches us, and it stays
          on the record either way.
        </p>
      </header>

      {/* The record, as four numbers. Ordered by how much they should move a
          pharmacy: the one that needs them today first, money last. */}
      <div className="mb-5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Tile
          label="Not answered"
          value={data ? String(data.unanswered) : null}
          tone="caution"
          note={data && data.unanswered > 0 ? 'Your side is still read' : 'All answered'}
          testId="complaints-awaiting"
        />
        <Tile
          label="With us"
          value={data ? String(data.answered) : null}
          tone="quiet"
          note="You have answered these"
        />
        <Tile
          label="Settled"
          value={data ? String(data.settled) : null}
          tone="quiet"
          note={data ? `${data.notUpheld} not upheld` : undefined}
        />
        <Tile
          label="Refunded"
          value={data ? data.refundedDisplay : null}
          tone="quiet"
          mono
          note={
            counts.refunded === 0
              ? 'Taken from your settlements'
              : `From ${counts.refunded} claim${counts.refunded === 1 ? '' : 's'}`
          }
        />
      </div>

      {/* Shelves, not filters: each one is a different thing to do. */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {SHELVES.map((one) => (
          <button
            key={one.key}
            type="button"
            onClick={() => setShelf(one.key)}
            data-testid={`shelf-${one.key}`}
            className={cn(
              'min-h-9 rounded-full border px-3 text-[12.5px] font-semibold',
              shelf === one.key
                ? 'border-blue bg-blue text-white'
                : 'border-line bg-white text-muted hover:text-ink',
            )}
          >
            {one.label}
            <span className="ml-1.5 opacity-70">{counts[one.key]}</span>
          </button>
        ))}
      </div>

      {/* The box that has to still work in three years and two thousand rows. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="relative min-w-[200px] flex-1">
          <span className="sr-only">Search complaints</span>
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
          />
          <input
            type="search"
            value={search}
            data-testid="complaints-search"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Reference, order, buyer, reason or amount"
            className="min-h-11 w-full rounded-btn border border-line bg-white pl-9 pr-3 text-[14px] text-ink outline-none focus:border-blue"
          />
        </label>

        <label>
          <span className="sr-only">Sort complaints</span>
          <select
            value={sort}
            data-testid="complaints-sort"
            onChange={(event) => setSort(event.target.value as Sort)}
            className="min-h-11 rounded-btn border border-line bg-white px-3 text-[13px] font-semibold text-ink outline-none focus:border-blue"
          >
            {SORTS.map((one) => (
              <option key={one.key} value={one.key}>
                {one.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <div className="space-y-1.5">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-[58px] w-full" radius="card" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          title={
            searching
              ? 'Nothing matches that'
              : shelf === 'needs_you'
                ? 'Everything is answered'
                : shelf === 'all'
                  ? 'No complaints yet'
                  : 'Nothing on this shelf'
          }
          body={
            searching
              ? 'Try a reference, an order number, or part of the buyer’s name.'
              : shelf === 'needs_you'
                ? 'When a buyer reports something about one of your orders, it lands here the moment it is filed.'
                : 'Everything you have ever been sent is under Everything.'
          }
          action={
            searching ? (
              <Button variant="grey" onClick={() => setSearch('')}>
                Clear the search
              </Button>
            ) : shelf === 'all' ? undefined : (
              <Button variant="grey" onClick={() => setShelf('all')}>
                Show everything
              </Button>
            )
          }
        />
      ) : (
        <>
          {/* Column headings, on the widths where there are columns. */}
          <div className="hidden items-center gap-3 px-3.5 pb-1.5 lg:flex">
            <span className="flex-1 font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              Complaint
            </span>
            <span className="w-[132px] font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              Order
            </span>
            <span className="w-[92px] text-right font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              Claimed
            </span>
            <span className="w-[88px] text-right font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              Reported
            </span>
            <span className="w-[156px] font-mono text-[10px] uppercase tracking-[0.1em] text-faint">
              State
            </span>
            <span className="w-[15px]" aria-hidden="true" />
          </div>

          <div className="overflow-hidden rounded-card border border-line bg-white">
            {shown.map((one) => (
              <ComplaintRow key={one.reference} complaint={one} onOpen={() => setOpen(one)} />
            ))}
          </div>

          <p className="m-0 mt-3 text-[11.5px] text-faint">
            Showing {shown.length} of {counts.all}
            {searching ? ' that match' : ''}.
          </p>
        </>
      )}

      {open ? (
        <DetailSheet
          complaint={open}
          onClose={() => setOpen(null)}
          onAnswered={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

// ── One row ─────────────────────────────────────────────────────────────────

function ComplaintRow({
  complaint,
  onOpen,
}: {
  complaint: SellerComplaintRow;
  onOpen: () => void;
}) {
  const needsYou = complaint.unanswered;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`complaint-${complaint.reference}`}
      className={cn(
        'flex w-full min-h-11 flex-wrap items-center gap-x-3 gap-y-1 border-b border-line px-3.5 py-2.5 text-left last:border-b-0',
        needsYou ? 'bg-caution-tint/40 hover:bg-caution-tint/70' : 'bg-white hover:bg-paper',
      )}
    >
      <span className="min-w-0 flex-1 basis-[180px]">
        <span className="block truncate text-[13.5px] font-bold leading-tight text-ink">
          {complaint.reason_label}
        </span>
        <span className="mt-0.5 block truncate text-[12px] text-muted">
          {complaint.buyer ?? 'A buyer'} · <span className="font-mono">{complaint.reference}</span>
        </span>
      </span>

      <span className="shrink-0 font-mono text-[11.5px] text-muted lg:w-[132px]">
        {complaint.order_reference}
      </span>

      <span className="shrink-0 font-mono text-[13px] font-bold tabular-nums text-ink lg:w-[92px] lg:text-right">
        {complaint.claimed_display}
      </span>

      <span className="shrink-0 text-[11.5px] text-faint lg:w-[88px] lg:text-right">
        {when(complaint.reported_at)}
      </span>

      <span className="flex shrink-0 items-center lg:w-[156px]">
        <StateChip complaint={complaint} />
      </span>

      <Icon name="chevron" size={15} strokeWidth={2} className="hidden shrink-0 text-muted lg:block" />
    </button>
  );
}

function StateChip({ complaint }: { complaint: SellerComplaintRow }) {
  if (complaint.unanswered) {
    return (
      <span className="inline-flex items-center gap-1 rounded-pill bg-caution-tint px-2 py-[3px] text-[11px] font-bold text-caution-ink">
        <Icon name="message" size={11} strokeWidth={2.2} className="shrink-0" />
        Add your side
      </span>
    );
  }

  if (complaint.decided_at) {
    return (
      <span className="truncate rounded-pill bg-paper px-2 py-[3px] text-[11px] font-medium text-muted">
        {complaint.resolution_label ?? 'Settled'}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 rounded-pill bg-blue-tint/60 px-2 py-[3px] text-[11px] font-medium text-blue">
      <Icon name="clock" size={11} strokeWidth={2.2} className="shrink-0" />
      With the reviewer
    </span>
  );
}

/** "3 days ago" beats a date nobody converts in their head. */
function when(iso: string | null): string {
  if (!iso) {
    return '';
  }

  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

  if (days <= 0) {
    return 'Today';
  }

  if (days === 1) {
    return 'Yesterday';
  }

  if (days < 30) {
    return `${days} days ago`;
  }

  // Past a month the relative form stops helping: "412 days ago" is not a date
  // anybody can act on, and this record is meant to be read in years.
  return new Date(iso).toLocaleDateString('en-NG', {
    day: 'numeric',
    month: 'short',
    year: days > 300 ? 'numeric' : undefined,
  });
}

function Tile({
  label,
  value,
  note,
  tone,
  mono = false,
  testId,
}: {
  label: string;
  value: string | null;
  note?: string;
  tone: 'caution' | 'quiet';
  mono?: boolean;
  testId?: string;
}) {
  // Amber only when something is actually waiting. A permanently amber tile is
  // wallpaper within a week.
  const loud = tone === 'caution' && value !== null && value !== '0';

  return (
    <div
      data-testid={testId}
      className={cn(
        'rounded-card border px-3.5 py-3',
        loud ? 'border-caution bg-caution-tint' : 'border-line bg-white',
      )}
    >
      <p
        className={cn(
          'm-0 font-mono text-[10px] uppercase tracking-[0.1em]',
          loud ? 'text-caution-ink' : 'text-faint',
        )}
      >
        {label}
      </p>

      {value === null ? (
        <Skeleton className="mt-1.5 h-6 w-14" />
      ) : (
        <p
          className={cn(
            'm-0 mt-0.5 font-bold tabular-nums',
            mono ? 'font-mono text-[17px]' : 'font-display text-[22px]',
            loud ? 'text-caution-ink' : 'text-ink',
          )}
        >
          {value}
        </p>
      )}

      {note ? (
        <p
          className={cn(
            'm-0 mt-0.5 text-[11px] leading-snug',
            loud ? 'text-caution-ink/80' : 'text-faint',
          )}
        >
          {note}
        </p>
      ) : null}
    </div>
  );
}

// ── The detail ──────────────────────────────────────────────────────────────

/**
 * One complaint in full, over the list.
 *
 * A sheet rather than an expanding row: the answer box needs room to think in,
 * and growing a row shoves every row below it down, which loses the place of
 * whoever was reading.
 */
function DetailSheet({
  complaint,
  onClose,
  onAnswered,
}: {
  complaint: SellerComplaintRow;
  onClose: () => void;
  onAnswered: () => void;
}) {
  const queryClient = useQueryClient();
  const [response, setResponse] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['seller', 'complaint', complaint.reference],
    queryFn: ({ signal }) => api.sellerComplaint(complaint.reference, signal),
  });

  const reply = useMutation({
    mutationFn: () => api.respondToComplaint(complaint.reference, response.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['seller'] });
      setResponse('');
      onAnswered();
    },
  });

  const error = reply.error instanceof ApiError ? reply.error : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 border-0 bg-ink/40 p-0"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Complaint ${complaint.reference}`}
        className="relative max-h-[88dvh] w-full overflow-y-auto rounded-t-card border border-line bg-white sm:max-w-[560px] sm:rounded-card"
      >
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-line bg-white px-4 py-3.5">
          <div className="min-w-0">
            <h2 className="m-0 font-display text-[18px] font-bold tracking-[-0.02em]">
              {complaint.reason_label}
            </h2>
            <p className="m-0 mt-0.5 font-mono text-[11px] text-muted">
              {complaint.reference} · {complaint.order_reference} · {complaint.claimed_display}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-btn bg-transparent text-muted hover:bg-paper"
          >
            <Icon name="x" size={18} strokeWidth={2} />
          </button>
        </div>

        <div className="px-4 py-4">
          {isLoading || !data ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <>
              <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                What the buyer said
              </p>
              <p className="m-0 mt-1.5 whitespace-pre-line text-[13.5px] leading-relaxed text-ink">
                {data.description || 'They did not write anything.'}
              </p>

              {data.items.length > 0 ? (
                <>
                  <p className="m-0 mt-4 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                    What you sent
                  </p>
                  <ul className="m-0 mt-1.5 list-none space-y-1 p-0">
                    {data.items.map((item, i) => (
                      <li key={i} className="text-[12.5px] text-ink">
                        {item.name}
                        {/* The batch is the thing a pharmacy can act on this
                            afternoon — pull the rest of it off the shelf. */}
                        {item.batch_no ? (
                          <span className="ml-1.5 font-mono text-[11.5px] text-muted">
                            batch {item.batch_no}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          )}

          {complaint.your_response ? (
            <div className="mt-4 border-t border-line pt-3">
              <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                What you told us
              </p>
              <p className="m-0 mt-1 whitespace-pre-line text-[12.5px] leading-relaxed text-ink">
                {complaint.your_response}
              </p>
            </div>
          ) : complaint.unanswered ? (
            <div className="mt-4 border-t border-line pt-4">
              <label className="block">
                <span className="mb-1.5 block text-[12.5px] font-medium text-ink">
                  What happened from your side?
                </span>
                <textarea
                  value={response}
                  rows={4}
                  onChange={(event) => setResponse(event.target.value)}
                  placeholder="Anything that would change how we read this — what you dispensed, how it was packed, whether you have seen it before."
                  className="w-full rounded-inp border border-line bg-white px-3 py-2 text-[13.5px] leading-relaxed text-ink outline-none focus:border-blue"
                />
              </label>

              {error ? (
                <p role="alert" className="m-0 mt-2 text-[12.5px] font-medium text-danger">
                  {error.fieldErrors.response?.[0] ?? error.message}
                </p>
              ) : null}

              <Button
                size="sm"
                className="mt-3"
                disabled={reply.isPending}
                onClick={() => reply.mutate()}
              >
                {reply.isPending ? 'Sending…' : 'Send our side'}
              </Button>

              <p className="m-0 mt-2 text-[11.5px] leading-snug text-faint">
                We decide this under the returns policy and do not hold it for a reply. Whatever you
                write is read if it reaches us first, and stays on the claim either way.
              </p>
            </div>
          ) : null}

          {complaint.decision_note ? (
            <div className="mt-4 border-t border-line pt-3">
              <p className="m-0 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
                What we decided
              </p>
              {complaint.resolution_label ? (
                <p className="m-0 mt-1 text-[12.5px] font-bold text-ink">
                  {complaint.resolution_label}
                </p>
              ) : null}
              <p className="m-0 mt-1 whitespace-pre-line text-[12.5px] leading-relaxed text-ink">
                {complaint.decision_note}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
